// src/contexts/NotificationsContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { fetchNotificationsService, updateNotificationService } from '@/services/notifications/notificationService';

const NotificationsContext = createContext<any | null>(null);

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error(
            "useNotifications deve essere utilizzato all'interno di un NotificationsProvider",
        );
    }
    return context;
};

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { organization } = useAuth();

    const fetchNotifications = async () => {
        try {
            setLoading(true);

            if (organization?.id) {
                const result = await fetchNotificationsService(organization.id);
                if (result.success) {
                    setNotifications(result.data);
                } else {
                    console.error('Errore service:', result.error);
                }
            }
        } catch (error: any) {
            console.error('Errore eccezione:', error?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (organization?.id) {
            fetchNotifications();
        }
    }, [organization?.id]);

    const updateNotification = async (id: number, read: boolean) => {
        await updateNotificationService(id, read);
    };

    const markAsRead = async (id: number) => {
        await updateNotification(id, true);
        setNotifications((prev) =>
            prev.map((notification) =>
                notification.id === id
                    ? { ...notification, read: true }
                    : notification,
            ),
        );
    };

    const markAllAsRead = async () => {
        const unreadNotifications = notifications.filter((n) => !n.read);
        await Promise.all(
            unreadNotifications.map((n) => updateNotification(n.id, true)),
        );
        setNotifications((prev) =>
            prev.map((notification) => ({ ...notification, read: true })),
        );
    };

    const value = {
        notifications,
        loading,
        markAsRead,
        markAllAsRead,
        fetchNotifications,
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};